import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router';

import { api, type ApiRequestConfig } from 'api/v1/api';
import { isAuthSessionActive, useAuthSession } from 'hooks/useAuthSession';
import { fetchProfileByType } from 'modules/Profile/model/services';
import { resolveProfileType } from 'utils/resolveProfileType';
import { useChatWebSocket } from 'modules/ChatV2/store/ChatWebSocketContext';

import type { NotificationsResponse } from './useNotifications';

const SYNC_KEY = ['profile-realtime-sync'];

/**
 * Периодически подтягивает профиль и уведомления без перезагрузки страницы,
 * а также реагирует на WebSocket события для мгновенного обновления.
 */
export const useRealtimeProfileSync = (enabled = true) => {
  const queryClient = useQueryClient();
  const { pathname } = useLocation();
  const { data: session } = useAuthSession();
  const profileType = resolveProfileType({
    sessionRoles: session?.roles,
    pathname,
  });
  const isEnabled = enabled && isAuthSessionActive(session) && Boolean(profileType);
  const { subscribeGlobal } = useChatWebSocket();

  useEffect(() => {
    if (!isEnabled) return;
    
    // Подписка на глобальные события пользователя (notification, profile_sync и т.д.)
    const unsubscribe = subscribeGlobal((event) => {
      if (['notification', 'profile_sync', 'bulletin_sync'].includes(event.type)) {
        void queryClient.invalidateQueries({ queryKey: SYNC_KEY });
      }
    });
    
    return () => unsubscribe();
  }, [isEnabled, subscribeGlobal, queryClient]);

  return useQuery({
    queryKey: SYNC_KEY,
    queryFn: async () => {
      const [profile, notifications] = await Promise.all([
        profileType
          ? fetchProfileByType(profileType).catch(() => null)
          : Promise.resolve(null),
        api
          .get('/notifications', { skipGlobalErrorHandler: true } as ApiRequestConfig)
          .then((response) => response.data as NotificationsResponse)
          .catch(() => null),
        api
          .post('/users/presence', {}, { skipGlobalErrorHandler: true } as ApiRequestConfig)
          .catch(() => null),
      ]);

      if (profile && profileType) {
        const prevProfile = queryClient.getQueryData<{
          profile_photo_token?: string | null;
          verification_status?: string | null;
          verification_comment?: string | null;
        }>(['profile', profileType]);

        const photoTokenChanged =
          profile?.profile_photo_token !== undefined &&
          prevProfile?.profile_photo_token !== profile.profile_photo_token;
        const verificationChanged =
          profile?.verification_status !== prevProfile?.verification_status ||
          profile?.verification_comment !== prevProfile?.verification_comment;

        queryClient.setQueryData(['profile', profileType], profile);

        if (photoTokenChanged) {
          queryClient.invalidateQueries({ queryKey: ['photos-full'] });
          queryClient.invalidateQueries({ queryKey: ['tutor-profile-readiness'] });
          queryClient.invalidateQueries({ queryKey: ['student-profile-readiness'] });
        }

        if (photoTokenChanged || verificationChanged) {
          queryClient.invalidateQueries({ queryKey: ['verification-status'] });
        }
      }

      if (notifications) {
        queryClient.setQueryData(['notifications'], notifications);
      }

      queryClient.invalidateQueries({ queryKey: ['bulletins'] });
      void queryClient.invalidateQueries({ queryKey: ['chat'] });
      void queryClient.invalidateQueries({ queryKey: ['student_chat_bulletin_threads'] });
      void queryClient.invalidateQueries({ queryKey: ['student_bulletin_tutors'] });
      if (session?.roles?.includes(1)) {
        queryClient.invalidateQueries({ queryKey: ['my_responses'] });
      }

      return { profile, notifications };
    },
    enabled: isEnabled,
    refetchOnWindowFocus: true,
    refetchIntervalInBackground: false,
  });
};
